/**
 * RecentlyLearnedRules — iter1100
 *
 * Compact audit panel showing every rule the platform learned/reinforced
 * for THIS business in the last 30 days, grouped by trigger source. Users
 * can undo any rule with one click. Consumes GET /ai-corrections/learning/recent.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Undo2, Sparkles, UserCheck, Wand2, Users } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_META = {
  user_approved:      { label: 'You approved',   Icon: UserCheck, tint: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  user_reclassified:  { label: 'You reclassified', Icon: Wand2,   tint: 'bg-blue-50 text-blue-700 border-blue-200' },
  ai_auto_learn:      { label: 'AI auto-learned', Icon: Sparkles, tint: 'bg-amber-50 text-amber-700 border-amber-200' },
  invoice_auto_learn: { label: 'From an invoice', Icon: Sparkles, tint: 'bg-purple-50 text-purple-700 border-purple-200' },
  community:          { label: 'Community',      Icon: Users,    tint: 'bg-slate-50 text-slate-700 border-slate-200' },
  unknown:            { label: 'Other',          Icon: Sparkles, tint: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function RecentlyLearnedRules() {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState(null);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: '30', limit: '50' });
      if (filter) params.set('source', filter);
      const { data } = await axios.get(`${API}/ai-corrections/learning/recent?${params}`);
      setRules(data.rules || []);
      setCounts(data.counts_by_source || {});
    } catch (err) {
      console.error('[recently-learned] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const undoRule = async (rule) => {
    if (!rule?.id) return;
    try {
      await axios.delete(`${API}/ai-corrections/training-examples/${rule.id}`);
      toast.success('Rule removed. Future transactions will ask again.');
      setRules(prev => prev.filter(r => r.id !== rule.id));
    } catch {
      toast.error('Could not undo the rule.');
    }
  };

  const totalCount = rules.length;
  const chipSources = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <Card data-testid="recently-learned-rules">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            What your AI learned lately
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {totalCount} rule{totalCount === 1 ? '' : 's'} · last 30 days
          </span>
        </div>
        {chipSources.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2" data-testid="recently-learned-chips">
            <button
              type="button"
              onClick={() => setFilter(null)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                filter === null ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
              data-testid="recently-learned-chip-all"
            >
              All · {Object.values(counts).reduce((a, b) => a + b, 0)}
            </button>
            {chipSources.map(([src, n]) => {
              const meta = SOURCE_META[src] || SOURCE_META.unknown;
              return (
                <button
                  key={src}
                  type="button"
                  onClick={() => setFilter(src)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition inline-flex items-center gap-1 ${
                    filter === src ? 'bg-slate-900 text-white border-slate-900' : `${meta.tint} hover:border-slate-400`
                  }`}
                  data-testid={`recently-learned-chip-${src}`}
                >
                  <meta.Icon className="w-3 h-3" />
                  {meta.label} · {n}
                </button>
              );
            })}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : rules.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No new rules in the last 30 days.
          </p>
        ) : (
          <div className="space-y-2" data-testid="recently-learned-list">
            {rules.map((r, i) => {
              const meta = SOURCE_META[r.source] || SOURCE_META.unknown;
              return (
                <div
                  key={r.id || i}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition"
                  data-testid={`recently-learned-row-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={`${meta.tint} text-[10px] uppercase tracking-wide`}>
                        <meta.Icon className="w-3 h-3 mr-1" />{meta.label}
                      </Badge>
                      <span className="text-xs text-slate-400">{timeAgo(r.created_at)}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {r.counterparty || r.description || 'Unnamed rule'}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {r.direction === 'inflow' ? '↓' : '↑'} {r.classification || r.gl_name || 'GL rule'}
                      {r.gl_code ? ` · ${r.gl_code}` : ''}
                      {r.times_matched ? ` · matched ${r.times_matched}×` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => undoRule(r)}
                    className="shrink-0 text-slate-500 hover:text-red-600"
                    title="Remove this rule"
                    data-testid={`recently-learned-undo-${i}`}
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
